/**
 * Tournament Fixture repository (ADR-031 P3).
 * Path: arenas/{arenaId}/events/{eventId}/fixtures/{fixtureId}
 * Event owns fixtures; Draw Engine still consumes assembled ManagedDraw.fixtures.
 */

import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  setDoc,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { touchEvent } from "./tournamentEventRepository";

function fixturesCol(arenaId, eventId) {
  return collection(db, "arenas", arenaId, "events", eventId, "fixtures");
}

function fixtureRef(arenaId, eventId, fixtureId) {
  return doc(db, "arenas", arenaId, "events", eventId, "fixtures", fixtureId);
}

/**
 * @param {string} arenaId
 * @param {string} eventId
 * @param {{ drawId?: string }} [opts]
 */
export async function listFixturesForEvent(arenaId, eventId, opts = {}) {
  if (!arenaId || !eventId) return [];
  let snap;
  if (opts.drawId) {
    try {
      const q = query(fixturesCol(arenaId, eventId), where("drawId", "==", opts.drawId));
      snap = await getDocs(q);
    } catch {
      snap = await getDocs(fixturesCol(arenaId, eventId));
    }
  } else {
    snap = await getDocs(fixturesCol(arenaId, eventId));
  }
  let rows = snap.docs.map((d) => ({
    fixtureId: d.id,
    eventId,
    arenaId,
    ...d.data(),
  }));
  if (opts.drawId) {
    rows = rows.filter((f) => f.drawId === opts.drawId);
  }
  return rows.sort(
    (a, b) =>
      (a.roundIndex ?? 0) - (b.roundIndex ?? 0) ||
      (a.matchIndex ?? 0) - (b.matchIndex ?? 0)
  );
}

/**
 * @param {string} arenaId
 * @param {string[]} eventIds
 */
export async function listFixturesForArena(arenaId, eventIds) {
  if (!arenaId) return [];
  const ids = Array.isArray(eventIds) ? eventIds : [];
  const nested = await Promise.all(ids.map((eventId) => listFixturesForEvent(arenaId, eventId)));
  return nested.flat();
}

/**
 * @param {string} arenaId
 * @param {string} eventId
 * @param {object} fixture
 */
export async function upsertFixture(arenaId, eventId, fixture) {
  const fixtureId = fixture.fixtureId;
  if (!arenaId || !eventId || !fixtureId) {
    throw new Error("arenaId, eventId, and fixtureId required.");
  }
  await setDoc(
    fixtureRef(arenaId, eventId, fixtureId),
    {
      ...fixture,
      fixtureId,
      eventId,
      arenaId,
      drawId: fixture.drawId || null,
      updatedAt: serverTimestamp(),
      createdAt: fixture.createdAt || serverTimestamp(),
    },
    { merge: true }
  );
  return { ...fixture, fixtureId, eventId, arenaId };
}

/**
 * Replace fixtures for a draw (used after generate / format change).
 * @param {string} arenaId
 * @param {string} eventId
 * @param {string} drawId
 * @param {object[]} fixtures
 */
export async function replaceFixturesForDraw(arenaId, eventId, drawId, fixtures) {
  const existing = await listFixturesForEvent(arenaId, eventId, { drawId });
  const nextIds = new Set((fixtures || []).map((f) => f.fixtureId));
  await Promise.all(
    (fixtures || []).map((f) =>
      upsertFixture(arenaId, eventId, { ...f, drawId, eventId })
    )
  );
  await Promise.all(
    existing
      .filter((f) => !nextIds.has(f.fixtureId))
      .map((f) => deleteDoc(fixtureRef(arenaId, eventId, f.fixtureId)))
  );
  await touchEvent(arenaId, eventId);
  return listFixturesForEvent(arenaId, eventId, { drawId });
}

/**
 * @param {string} arenaId
 * @param {string} eventId
 * @param {string} fixtureId
 */
export async function deleteFixture(arenaId, eventId, fixtureId) {
  if (!arenaId || !eventId || !fixtureId) return;
  await deleteDoc(fixtureRef(arenaId, eventId, fixtureId));
  await touchEvent(arenaId, eventId);
}
